import * as Sentry from '@sentry/react';
import type { Scope } from '@sentry/core';

type SessionSentryContext = {
  event: string;
  tenantId?: string;
  userId?: string;
  sessionId?: string;
  authProvider?: string;
  reason?: string;
  extra?: Record<string, unknown>;
};

function safeToString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  try {
    return String(value);
  } catch {
    return undefined;
  }
}

function applySessionContext(scope: Scope, ctx: SessionSentryContext): void {
  scope.setTag('ui.session.event', ctx.event);

  const tenantId = safeToString(ctx.tenantId);
  if (tenantId) scope.setTag('tenantId', tenantId);

  const authProvider = safeToString(ctx.authProvider);
  if (authProvider) scope.setTag('authProvider', authProvider);

  scope.setExtra('sessionId', safeToString(ctx.sessionId) ?? null);
  scope.setExtra('userId', safeToString(ctx.userId) ?? null);
  scope.setExtra('reason', safeToString(ctx.reason) ?? null);
  if (ctx.extra) scope.setExtra('extra', ctx.extra);
}

function buildConsoleContext(
  ctx: SessionSentryContext,
): Record<string, unknown> {
  return {
    timestamp: new Date().toISOString(),
    event: ctx.event,
    tenantId: safeToString(ctx.tenantId) ?? null,
    userId: safeToString(ctx.userId) ?? null,
    sessionId: safeToString(ctx.sessionId) ?? null,
    authProvider: safeToString(ctx.authProvider) ?? null,
    reason: safeToString(ctx.reason) ?? null,
    extra: ctx.extra ?? null,
  };
}

function logSessionToConsole(
  message: string,
  level: 'info' | 'warning' | 'error',
  ctx: SessionSentryContext,
): void {
  const prefix = `[MOS Session][${ctx.event}] ${message}`;
  const payload = buildConsoleContext(ctx);

  if (level === 'error') {
    console.error(prefix, payload);
    return;
  }

  if (level === 'warning') {
    console.warn(prefix, payload);
    return;
  }

  console.info(prefix, payload);
}

export function captureSessionMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  ctx: SessionSentryContext,
): void {
  logSessionToConsole(message, level, ctx);
}

export function captureSessionException(
  error: unknown,
  ctx: SessionSentryContext,
): void {
  const errorMessage =
    error instanceof Error
      ? error.message
      : (safeToString(error) ?? 'Unknown error');
  console.error(`[MOS Session][${ctx.event}] ${errorMessage}`, {
    ...buildConsoleContext(ctx),
    error,
  });

  Sentry.withScope((scope) => {
    applySessionContext(scope, ctx);
    Sentry.captureException(error);
  });
}
